export const STORAGE_KEYS = {
  settings: {
    hardMode: 'crackle:settings:hardMode',
    darkMode: 'crackle:settings:darkMode',
    animationMode: 'crackle:settings:animationMode',
  },
  game: {
    current: 'crackle:game:current',
    history: 'crackle:game:history',
  },
  flags: {
    debug: 'crackle:flags:debug',
    dev: 'crackle:flags:dev',
    testMode: 'crackle:flags:testMode',
  },
  telemetry: {
    clientId: 'crackle:telemetry:clientId',
  },
  consent: {
    gdprAccepted: 'crackle:consent:gdprAccepted',
  },
};

// Enable debug logs only when explicitly requested via localStorage or runtime toggle.
let DEBUG_ENABLED = false;
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    const debugFlag =
      window.localStorage.getItem(STORAGE_KEYS.flags.debug) ??
      window.localStorage.getItem('CRACKLE_DEBUG');
    if (debugFlag === '1') {
      DEBUG_ENABLED = true;
    }
  }
} catch (e) { /* ignore if localStorage not available */ }

export function setDebug(enabled) {
  DEBUG_ENABLED = enabled;
}

export function debugLog(...args) {
  if (DEBUG_ENABLED) {
    const timestamp = new Date().toISOString();
    console.log(`🧊 [${timestamp}]`, ...args);
  }
}

export function loadFromLocalStorage(key, fallback) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch (err) {
    console.error(`Failed to load ${key}:`, err);
    return fallback;
  }
}

export function loadRawLocalStorage(key, fallback) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (err) {
    console.error(`Failed to load ${key}:`, err);
    return fallback;
  }
}

export function saveToLocalStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`Failed to save ${key}:`, err);
  }
}

export function removeFromLocalStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.error(`Failed to remove ${key}:`, err);
  }
}

export function migrateLegacyLocalStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return;

  const migrations = [
    { oldKey: 'hardMode', newKey: STORAGE_KEYS.settings.hardMode },
    { oldKey: 'darkMode', newKey: STORAGE_KEYS.settings.darkMode },
    { oldKey: 'ANIMATION_MODE', newKey: STORAGE_KEYS.settings.animationMode },
    { oldKey: 'currentGame', newKey: STORAGE_KEYS.game.current },
    { oldKey: 'gameHistory', newKey: STORAGE_KEYS.game.history },
    { oldKey: 'CRACKLE_DEBUG', newKey: STORAGE_KEYS.flags.debug },
    { oldKey: 'CRACKLE_DEV', newKey: STORAGE_KEYS.flags.dev },
    { oldKey: 'TESTMODE', newKey: STORAGE_KEYS.flags.testMode },
  ];

  migrations.forEach(({ oldKey, newKey }) => {
    const newValue = localStorage.getItem(newKey);
    if (newValue === null || newValue === undefined) {
      const oldValue = localStorage.getItem(oldKey);
      if (oldValue !== null && oldValue !== undefined) {
        localStorage.setItem(newKey, oldValue);
      }
    }
    if (localStorage.getItem(oldKey) !== null) {
      localStorage.removeItem(oldKey);
    }
  });
}

export function stripAnswersFromGame(game) {
  if (!game || !Array.isArray(game.levels)) return game;
  const levels = game.levels.map(level => {
    if (!level || typeof level !== 'object') return level;
    const hasAnswer = Object.prototype.hasOwnProperty.call(level, 'answer');
    const hasScore = Object.prototype.hasOwnProperty.call(level, 'answerScore');
    if (!hasAnswer && !hasScore) return level;
    const { answer, answerScore, ...rest } = level;
    return rest;
  });
  return { ...game, levels };
}

export function stripAnswersFromHistory(history) {
  if (!Array.isArray(history)) return history;
  return history.map(stripAnswersFromGame);
}

export function getOrCreateClientId() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const key = STORAGE_KEYS.telemetry.clientId;
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = (window.crypto && typeof window.crypto.randomUUID === 'function')
    ? window.crypto.randomUUID()
    : `cid_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  localStorage.setItem(key, id);
  return id;
}

export const medalMap = {
  0: '🥉',
  1: '🥈',
  2: '🥇'
};

export function generateAnswer(order = 'ascending', allowRepeats = false) {
  const hexDigits = '0123456789ABCDEF'.split('');
  const digits = [];

  while (digits.length < 5) {
    const randDigit = hexDigits[Math.floor(Math.random() * hexDigits.length)];

    if (allowRepeats || !digits.includes(randDigit)) {
      digits.push(randDigit);
    }
  }

  if (order === 'ascending') {
    digits.sort((a, b) => parseInt(a, 16) - parseInt(b, 16));
  } else if (order === 'descending') {
    digits.sort((a, b) => parseInt(b, 16) - parseInt(a, 16));
  }

  return digits;
}

export function scoreAnswer(answer, order) {
  // use difficulty metrics to score the answer
  const m1Score = calculateMetricGap(answer);
  const m2Score = calculateMetricDescending(order);
  const score = m1Score * m2Score;

  return parseFloat(score.toFixed(5));
}

// on Skill Up, ensure the new code is harder to crack than the previous code.
export function generateHarderAnswer(
  orderRule,
  allowDescending,
  allowRepeats,
  previousScore,
  minScoreIncrement,
  maxScoreIncrement,
  maxAttempts = 200 // Default max attempts
) {
  if (typeof previousScore !== 'number' || typeof minScoreIncrement !== 'number') {
    return null;
  }

  let bestScoreSoFar = -Infinity;
  let bestAnswer = null;
  let bestOrder = null;

  let attempts = 0;

  for (attempts = 1; attempts <= maxAttempts; attempts++) {
    let orderForThisAttempt;

    // Determine order for THIS attempt
    if (orderRule === null) { // choose randomly
      orderForThisAttempt = Math.random() < 0.5 ? 'ascending' : 'descending';
    } else if (orderRule === 'ascending' || orderRule === 'descending') {
      orderForThisAttempt = orderRule;
    } else {
      console.error(`[generateHarderAnswer] Invalid orderRule: ${orderRule}. Defaulting to ascending.`);
      orderForThisAttempt = 'ascending'; // Fallback for unexpected rule string
    }
    // --- End Determine order ---
    const candidateAnswer = generateAnswer(orderForThisAttempt, allowRepeats);
    const candidateScore = scoreAnswer(candidateAnswer, orderForThisAttempt);
    const candidateOrder = orderForThisAttempt;

    if (candidateScore > bestScoreSoFar) {
      bestScoreSoFar = candidateScore;
      bestAnswer = candidateAnswer;
      bestOrder = candidateOrder;
    }

    if (candidateScore > previousScore + minScoreIncrement && candidateScore <= previousScore + maxScoreIncrement) {
      return {
        answer: candidateAnswer,
        score: candidateScore,
        order: candidateOrder,
        attempts: attempts,
        achievedIncrement: true
      };
    } else if (candidateScore <= previousScore + minScoreIncrement) {
    } else if (candidateScore > previousScore + maxScoreIncrement) {
    }

    if (attempts % 20 === 0 && attempts < maxAttempts) { // Log progress occasionally
    }
  }

  // If loop finishes without meeting the increment criteria

  return {
    answer: bestAnswer,
    score: bestScoreSoFar,
    order: bestOrder,
    attempts: attempts - 1,
    achievedIncrement: false
  };
}

export function getFeedbackForGuess(guess, answer) {
  const normGuess = guess.split('').map(char => char.toUpperCase());
  const normAnswer = answer.split('').map(char => char.toUpperCase());

  const result = Array(5).fill('gray');
  const usedInAnswer = Array(5).fill(false);

  for (let i = 0; i < 5; i++) {
    if (normGuess[i] === normAnswer[i]) {
      result[i] = 'green';
      usedInAnswer[i] = true;
    }
  }

  for (let i = 0; i < 5; i++) {
    if (result[i] === 'green') continue;
    for (let j = 0; j < 5; j++) {
      if (!usedInAnswer[j] && normGuess[i] === normAnswer[j]) {
        result[i] = 'yellow';
        usedInAnswer[j] = true;
        break;
      }
    }
  }

  return result;
}

export function getCurrentScore(gameHistory = []) {
  const guessesUsed = gameHistory.reduce((sum, game) => {
    return sum + (game.guessesUsed || 0);
  }, 0);

  const guessesAllowed = gameHistory.reduce((sum, game) => {
    return sum + (game.guessesAllowed || 0);
  }, 0);

  return { guessesUsed, guessesAllowed };
}

export function buildSingleBlock(feedbackGrid) {
  return feedbackGrid
    .map(row => row.map(color => {
      if (color === 'green') return '🟩';
      if (color === 'yellow') return '🟨';
      if (color === 'gray') return '⬜';
      return '⬛'; // fallback
    }).join(''))
    .join('\n');
}

export function formatShareableString(summaryBlocks, levelsCleared, totalGuessesUsed, totalGuessesAllowed, didWinOverall, medalMap) {
  const scoreLine = didWinOverall
    ? `✅ Crackle: ${totalGuessesUsed}/${totalGuessesAllowed}`
    : `❌ Crackle: ${totalGuessesUsed}/${totalGuessesAllowed}`;

  const centeredBlocks = summaryBlocks.map((block, index) => {
    if (typeof block !== 'string' || block.trim() === '') return null; // Handle empty blocks
    const lines = block.split('\n').map(line => '  ' + line).join('\n');
    const medalOrX = (index < levelsCleared)
      ? ` ${medalMap[index] || ''}`
      : (index === summaryBlocks.length - 1 && !didWinOverall && summaryBlocks.length > levelsCleared) // Failed on this last shown block
        ? ` ❌`
        : '';
    return lines + medalOrX;
  }).filter(Boolean);

  return `${scoreLine}\n\n${centeredBlocks.join('\n───────────\n')}`;
}

//  Sort a given answer into ascending order
export function getAscendingAnswer(answer) {
  return answer
    .map(hexChar => parseInt(hexChar, 16))
    .sort((a, b) => a - b);
}

//Calculate the gaps between consecutive elements.
export function calculateGaps(answer) {
  if (!answer || answer < 2) {
    return [];
  }
  const gaps = [];
  for (let i = 0; i < answer.length - 1; i++) {
    gaps.push(answer[i + 1] - answer[i]);
  }
  return gaps;
}


// Calculate the standard deviation of the digits in an answer.
export function standardDeviation(answer) {
  if (!answer || answer.length === 0) {
    return 0;
  }
  const n = answer.length;
  const mean = answer.reduce((sum, value) => sum + value, 0) / n;
  const variance = answer.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / n;
  return Math.sqrt(variance);
}

//Calculates the Gap Irregularity Metric (M_gap).
export function calculateMetricGap(answer) {
  if (!answer || answer.length !== 5) {
    return 0;
  }

  const sortAnswer = getAscendingAnswer(answer);
  const gaps = calculateGaps(sortAnswer);

  if (gaps.length === 0) {
    return 0;
  }

  const stdDevGaps = standardDeviation(gaps);

  return stdDevGaps;
}

/**
 * Calculates the M_2 difficulty factor based on the answer mode.
 * Ascending order is the baseline (1.0).
 * Descending order is considered slightly more difficult (1.1).
 * Other modes can be added as needed.
 *
 * @param {string} answerMode - The mode of the answer (e.g., 'ascending', 'descending').
 * @returns {number} The M_2 difficulty factor.
 */
export function calculateMetricDescending(answerMode) {

  if (typeof answerMode !== 'string') {
    return 1.0;
  }

  const mode = answerMode.toLowerCase(); // Normalize to lowercase for robustness

  switch (mode) {
    case 'ascending':
      return 1.0;
    case 'descending':
      return 1.1;
    default:
      return 1.0;
  }
}

/**
 * Calculates game statistics from a list of finished games.
 * Returns an object with { total, hard } sub-objects.
 */
export function calculateStats(gameHistory = []) {
  const defaults = {
    played: 0,
    winPct: 0,
    currentStreak: 0,
    maxStreak: 0,
    medals: { gold: 0, silver: 0, bronze: 0, fail: 0 },
    history: []
  };

  if (!Array.isArray(gameHistory) || gameHistory.length === 0) {
    return { total: defaults, hard: defaults };
  }

  // Sort by timestamp ascending for streak calculations
  const sorted = [...gameHistory].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  const processHistory = (subset) => {
    if (subset.length === 0) return { ...defaults };

    let totalWins = 0;

    // Streak states for each achievement level
    const streaks = {
      global: { current: 0, max: 0, prevDate: null },
      gold: { current: 0, max: 0, prevDate: null },
      silver: { current: 0, max: 0, prevDate: null },
      bronze: { current: 0, max: 0, prevDate: null }
    };

    const medals = { gold: 0, silver: 0, bronze: 0, fail: 0 };

    const getUTCDate = (ts) => new Date(ts).toLocaleDateString('sv-SE', { timeZone: 'America/New_York' });
    const getDayDiff = (d1Str, d2Str) => {
      const d1 = new Date(d1Str);
      const d2 = new Date(d2Str);
      return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
    };

    subset.forEach((game) => {
      const levels = (game.levels || []).filter(l => l.completed).length;
      const isWin = levels === 3;
      const dateStr = getUTCDate(game.timestamp);

      // 1. Update Medal Counts
      if (levels >= 1) medals.bronze++;
      if (levels >= 2) medals.silver++;
      if (levels === 3) medals.gold++;
      if (levels === 0) medals.fail++;

      if (isWin) totalWins++;

      // 2. Update Streaks for each level
      const updateStreak = (key, achieved) => {
        const s = streaks[key];
        if (achieved) {
          if (!s.prevDate) {
            s.current = 1;
          } else {
            const diff = getDayDiff(s.prevDate, dateStr);
            if (diff <= 1) { // Same day or consecutive
              s.current++;
            } else {
              s.current = 1;
            }
          }
          if (s.current > s.max) s.max = s.current;
          s.prevDate = dateStr;
        } else {
          s.current = 0;
        }
      };

      updateStreak('global', isWin);
      updateStreak('gold', levels === 3);
      updateStreak('silver', levels >= 2);
      updateStreak('bronze', levels >= 1);
    });

    // Valid check for today/yesterday for all streaks
    const todayStr = getUTCDate(Date.now());

    // Find absolute latest game date in the FULL history to catch mode-switching breaks
    // (Since players get only one play per day, playing Easy today must break a Hard streak)
    const latestGameOverall = [...gameHistory].sort((a, b) => b.timestamp - a.timestamp)[0];
    const latestDateOverall = latestGameOverall ? getUTCDate(latestGameOverall.timestamp) : null;

    Object.keys(streaks).forEach(key => {
      const s = streaks[key];
      if (s.prevDate) {
        const diff = getDayDiff(s.prevDate, todayStr);
        // Reset if:
        // 1. Gap is > 1 day (standard miss)
        // 2. OR Gap is 1 day but they already played a different game today (mode switch)
        if (diff > 1 || (diff === 1 && latestDateOverall === todayStr)) {
          s.current = 0;
        }
      }
    });

    return {
      played: subset.length,
      winPct: Math.round((totalWins / subset.length) * 100),
      currentStreak: streaks.global.current,
      maxStreak: streaks.global.max,
      medals: {
        gold: { count: medals.gold, currentStreak: streaks.gold.current, maxStreak: streaks.gold.max },
        silver: { count: medals.silver, currentStreak: streaks.silver.current, maxStreak: streaks.silver.max },
        bronze: { count: medals.bronze, currentStreak: streaks.bronze.current, maxStreak: streaks.bronze.max },
        fail: { count: medals.fail }
      },
      history: [...subset].reverse().slice(0, 50)
    };
  };

  const totalStats = processHistory(sorted);
  const hardStats = processHistory(sorted.filter(g => g.hardMode));

  return {
    total: totalStats,
    hard: hardStats
  };
}
